import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAdminCodes, useAdminCodesSummary} from '../../hooks/useAdminCodes.ts'
import {
    AdminCodeItem,
    AdminCodeStep,
    AdminCodeType,
    AdminTourOperatorFilter,
} from '../../domain/entities/AdminCode.ts'
import {ALL_STEPS, AdminCodeStepPill, STEP_LABEL_KEY} from './AdminCodeStepPill.tsx'
import {AdminCodeHistorySidePanel} from './AdminCodeHistorySidePanel.tsx'
import {FilterDropdown} from '../agenciesTable/FilterDropdown.tsx'
import {b2bMarketFlag, b2bMarketLabel} from '../../utils/b2bMarketDisplay.ts'
import {formatDate} from '../../utils/formatDate.ts'
import {formatNumber} from '../../utils/formatNumber.ts'
import {initialsOf} from '../../utils/initials.ts'
import IconSearch from '../../assets/icons/search.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'
import IconEye from '../../assets/icons/eye.svg?react'
import IconSpinner from '../../assets/icons/spinner.svg?react'

const PAGE_SIZE = 25

const SegmentedControl = <T extends string>({options, value, onChange}: {
    options: {value: T; label: string}[]
    value: T
    onChange: (value: T) => void
}) => (
    <span className="inline-flex items-center rounded-full border border-neutralBlack-200 bg-white p-0.5 text-[13px]">
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className={`px-3 py-1.5 rounded-full whitespace-nowrap ${
                    value === option.value ? 'bg-neutralBlack-900 text-white' : 'text-neutralBlack-600 hover:text-neutralBlack-900'
                }`}
            >
                {option.label}
            </button>
        ))}
    </span>
)

export const AdminCodesContainer: React.FC = () => {
    const {t} = useTranslation('dashboard')

    const [searchInput, setSearchInput] = useState('')
    const [q, setQ] = useState('')
    const [stepFilter, setStepFilter] = useState<AdminCodeStep[]>([])
    const [tourOperator, setTourOperator] = useState<AdminTourOperatorFilter>('include')
    // 'all' and not 'unique': a print order placed against a generic code was
    // dropped from the default list, so support looked for it and concluded it
    // had never arrived. The segmented control still narrows it down.
    const [type, setType] = useState<AdminCodeType>('all')
    const [incidentOnly, setIncidentOnly] = useState(false)
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [page, setPage] = useState(0)
    const [openRow, setOpenRow] = useState<AdminCodeItem | null>(null)

    // Debounce free-text search before it hits the server-side filter.
    useEffect(() => {
        const timeout = setTimeout(() => setQ(searchInput.trim()), 350)
        return () => clearTimeout(timeout)
    }, [searchInput])

    useEffect(() => setPage(0), [q, stepFilter, tourOperator, type, incidentOnly, from, to])

    const filter = useMemo(() => ({
        tourOperator,
        type,
        steps: stepFilter,
        incidentOnly,
        from: from || undefined,
        to: to || undefined,
        q: q || undefined,
        page,
        size: PAGE_SIZE,
    }), [tourOperator, type, stepFilter, incidentOnly, from, to, q, page])

    const {codes, total, isLoadingCodes, isFetchingCodes} = useAdminCodes(filter)
    const {steps: summarySteps, isLoadingSummary} = useAdminCodesSummary([], tourOperator, type)
    // isLoadingCodes only covers the very first request (no cached data yet, so
    // there's nothing to show but the skeleton). Refetches after that — paging,
    // filtering, searching — keep the previous rows on screen (keepPreviousData)
    // but still need some feedback, otherwise the tab looks stuck while it updates.
    const isRefetchingCodes = isFetchingCodes && !isLoadingCodes

    const hasActiveFilters = !!q || stepFilter.length > 0 || incidentOnly || !!from || !!to
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const currentPage = Math.min(page, pageCount - 1)

    const clearFilters = () => {
        setSearchInput('')
        setStepFilter([])
        setIncidentOnly(false)
        setFrom('')
        setTo('')
    }

    const toggleStepFromSummary = (step: AdminCodeStep) => {
        setStepFilter((current) => current.includes(step) ? current.filter((s) => s !== step) : [...current, step])
    }

    return (
        <div className="flex flex-col bg-neutralBlack-100 w-full h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-4 flex-wrap mb-1">
                <div className="flex items-baseline gap-2.5 min-w-0">
                    <h1 className="text-[26px] font-semibold tracking-tight text-neutralBlack-900">{t('adminCodes.title')}</h1>
                    <span className="text-base font-medium text-neutralBlack-500">{formatNumber(total)}</span>
                    {isRefetchingCodes && <IconSpinner className="size-4 text-neutralBlack-400"/>}
                </div>
                <span className="flex-1 min-w-[24px]"/>
                <label className="flex items-center gap-2 bg-white border border-neutralBlack-200 rounded-xl px-3.5 h-10 w-[300px] max-w-[40vw] shadow-sm">
                    <IconSearch className="w-4 h-4 flex-none text-neutralBlack-500"/>
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t('adminCodes.searchPlaceholder')}
                        className="flex-1 min-w-0 border-0 outline-none bg-transparent text-sm text-neutralBlack-900"
                    />
                </label>
            </div>

            {/* Step KPI strip */}
            {isLoadingSummary ? (
                <div className="flex items-center gap-2 flex-wrap mt-3">
                    {Array.from({length: 5}).map((_, i) => (
                        <span key={i} className="h-[26px] w-24 rounded-full bg-neutralBlack-100 animate-pulse"/>
                    ))}
                </div>
            ) : summarySteps.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mt-3">
                    {summarySteps.map(({step, count}) => (
                        <button key={step} type="button" onClick={() => toggleStepFromSummary(step)} className="focus:outline-none">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs whitespace-nowrap border ${
                                stepFilter.includes(step) ? 'border-neutralBlack-900' : 'border-transparent'
                            }`}>
                                <AdminCodeStepPill step={step}/>
                                <span className="text-neutralBlack-500 -ml-1">{formatNumber(count)}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-2 flex-wrap mb-4 mt-3">
                <FilterDropdown
                    label={t('adminCodes.filterStepLabel')}
                    options={ALL_STEPS.map((step) => ({value: step, label: t(STEP_LABEL_KEY[step])}))}
                    selected={stepFilter}
                    onChange={(values) => setStepFilter(values as AdminCodeStep[])}
                />
                <SegmentedControl
                    value={tourOperator}
                    onChange={setTourOperator}
                    options={[
                        {value: 'include', label: t('adminCodes.tourOperatorInclude')},
                        {value: 'exclude', label: t('adminCodes.tourOperatorExclude')},
                        {value: 'only', label: t('adminCodes.tourOperatorOnly')},
                    ]}
                />
                <SegmentedControl
                    value={type}
                    onChange={setType}
                    options={[
                        {value: 'unique', label: t('adminCodes.typeUnique')},
                        {value: 'generic', label: t('adminCodes.typeGeneric')},
                        {value: 'all', label: t('adminCodes.typeAll')},
                    ]}
                />
                <button
                    type="button"
                    onClick={() => setIncidentOnly((v) => !v)}
                    className={`flex items-center gap-1.5 h-[34px] px-3.5 rounded-full border text-[13px] whitespace-nowrap ${
                        incidentOnly ? 'bg-danger border-danger text-white' : 'bg-white border-neutralBlack-200 text-neutralBlack-800 hover:bg-neutralBlack-50'
                    }`}
                >
                    {t('adminCodes.filterIncidentLabel')}
                </button>
                <label className="flex items-center gap-1.5 h-[34px] px-3 rounded-full border border-neutralBlack-200 bg-white text-[13px] text-neutralBlack-600">
                    {t('adminCodes.dateFromLabel')}
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="outline-none bg-transparent text-neutralBlack-900"/>
                </label>
                <label className="flex items-center gap-1.5 h-[34px] px-3 rounded-full border border-neutralBlack-200 bg-white text-[13px] text-neutralBlack-600">
                    {t('adminCodes.dateToLabel')}
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="outline-none bg-transparent text-neutralBlack-900"/>
                </label>
                {hasActiveFilters && (
                    <button type="button" onClick={clearFilters} className="text-[13px] text-neutralBlack-500 hover:text-neutralBlack-900 underline">
                        {t('agenciesTable.clearFilters')}
                    </button>
                )}
            </div>

            {/* Table / states */}
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <div className={`bg-white border border-neutralBlack-200 rounded-2xl shadow-sm overflow-hidden transition-opacity duration-150 ${isRefetchingCodes ? 'opacity-60' : ''}`}>

                    {isLoadingCodes ? (
                        <div>
                            {Array.from({length: 8}).map((_, i) => (
                                <div key={i} className="flex items-center gap-6 px-5 py-[15px] border-b border-neutralBlack-100 animate-pulse">
                                    <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-neutralBlack-100"/>
                                    <span className="flex flex-col gap-1.5 w-[180px]">
                                        <span className="w-[140px] h-2.5 rounded-full bg-neutralBlack-100"/>
                                        <span className="w-[96px] h-2 rounded-full bg-neutralBlack-50"/>
                                    </span>
                                    <span className="w-20 h-2.5 rounded-full bg-neutralBlack-100"/>
                                    <span className="flex-1"/>
                                    <span className="w-20 h-2.5 rounded-full bg-neutralBlack-100"/>
                                </div>
                            ))}
                        </div>
                    ) : codes.length === 0 ? (
                        <div className="flex flex-col items-center text-center py-[72px] px-8">
                            <span className="w-24 h-24 rounded-[20px] bg-neutralBlack-50 border border-dashed border-neutralBlack-300 flex items-center justify-center mb-6">
                                <IconEye className="w-8 h-8 text-neutralBlack-400"/>
                            </span>
                            <h2 className="text-[19px] font-semibold tracking-tight text-neutralBlack-900 mb-2">
                                {hasActiveFilters ? t('agenciesTable.noResultsWithFilters') : t('adminCodes.emptyTitle')}
                            </h2>
                            <p className="max-w-[380px] text-sm leading-relaxed text-neutralBlack-500 mb-6">
                                {hasActiveFilters ? t('agenciesTable.tryRemovingFilter') : t('adminCodes.emptyNoFiltersHelp')}
                            </p>
                            {hasActiveFilters && (
                                <button type="button" onClick={clearFilters} className="h-10 px-[18px] rounded-full bg-neutralBlack-900 text-white text-sm font-medium hover:bg-neutralBlack-800">
                                    {t('agenciesTable.clearFilters')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1080px] border-collapse text-sm">
                                    <thead>
                                        <tr>
                                            <Th align="left">{t('adminCodes.columnCode')}</Th>
                                            <Th align="left">{t('adminCodes.columnAgency')}</Th>
                                            <Th align="left">{t('adminCodes.columnTraveler')}</Th>
                                            <Th align="left">{t('adminCodes.columnTrip')}</Th>
                                            <Th align="left">{t('adminCodes.columnTripbook')}</Th>
                                            <Th align="left">{t('adminCodes.columnStep')}</Th>
                                            <Th align="left">{t('adminCodes.columnChanges')}</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* key por rowId, no por codeId: un código genérico
                                            aparece una vez por pedido y compartirían clave. */}
                                        {codes.map((row) => (
                                            <CodeRow key={row.rowId} row={row} onOpen={() => setOpenRow(row)}/>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center gap-3 flex-wrap px-5 py-3.5">
                                <span className="text-[13px] text-neutralBlack-500">
                                    {t('packs.paginationOf', {
                                        from: formatNumber(currentPage * PAGE_SIZE + 1),
                                        to: formatNumber(Math.min(total, (currentPage + 1) * PAGE_SIZE)),
                                        total: formatNumber(total),
                                    })}
                                </span>
                                <span className="flex-1 min-w-[12px]"/>
                                <button
                                    type="button"
                                    disabled={currentPage === 0}
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
                                >
                                    <IconChevronDown className="w-3 h-3 rotate-90"/>
                                </button>
                                <button
                                    type="button"
                                    disabled={currentPage >= pageCount - 1}
                                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
                                >
                                    <IconChevronDown className="w-3 h-3 -rotate-90"/>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {openRow && <AdminCodeHistorySidePanel row={openRow} onClose={() => setOpenRow(null)}/>}
        </div>
    )
}

const CodeRow: React.FC<{row: AdminCodeItem; onOpen: () => void}> = ({row, onOpen}) => {
    const {t} = useTranslation('dashboard')
    const flag = row.agency.market ? b2bMarketFlag(row.agency.market) : undefined
    const totalModifications = row.activity.tripModifications + row.activity.travelerModifications
    const hasIncident = row.incidents.cancelled || row.incidents.failed || row.incidents.returned

    return (
        <tr onClick={onOpen} className="cursor-pointer border-b border-neutralBlack-100 hover:bg-neutralBlack-50">
            <td className="px-5 py-3">
                <span className="flex flex-col gap-0.5">
                    <span className="font-medium text-neutralBlack-900 tracking-wide whitespace-nowrap">{row.code}</span>
                    <span className="flex items-center gap-1.5 flex-wrap">
                        {row.generic && (
                            <span className="inline-flex w-fit items-center rounded-full border border-dashed border-neutralBlack-300 px-2 py-0.5 text-[11px] text-neutralBlack-500">
                                {t('adminCodes.genericBadge')}
                            </span>
                        )}
                        {/* Sin esto, ver el mismo código en varias filas parece un
                            duplicado. Cada fila es un viaje distinto del código. */}
                        {(row.trip?.tripsForCode ?? 0) > 1 && (
                            <span className="inline-flex w-fit items-center rounded-full bg-neutralBlack-100 px-2 py-0.5 text-[11px] text-neutralBlack-600 whitespace-nowrap">
                                {t('adminCodes.tripsForCode', {count: row.trip!.tripsForCode})}
                            </span>
                        )}
                    </span>
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                <span className="flex items-center gap-2.5">
                    <span className="w-[30px] h-[30px] flex-none rounded-[9px] bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center text-[11px] font-semibold text-neutralBlack-800">
                        {initialsOf(row.agency.name ?? '?')}
                    </span>
                    <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm text-neutralBlack-900 whitespace-nowrap">{row.agency.name ?? '—'}</span>
                        {row.agency.market && (
                            <span className="flex items-center gap-1.5 text-xs text-neutralBlack-500">
                                {flag && <img src={flag} alt={row.agency.market} className="w-[14px] h-[10px] rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>}
                                {b2bMarketLabel(row.agency.market)}
                            </span>
                        )}
                    </span>
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                {row.traveler.name ? (
                    <span className="flex flex-col gap-0.5">
                        <span className="text-neutralBlack-900">{row.traveler.name}</span>
                        {row.traveler.email && <span className="text-xs text-neutralBlack-500">{row.traveler.email}</span>}
                    </span>
                ) : <span className="text-neutralBlack-400">{t('adminCodes.noTraveler')}</span>}
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                {row.trip ? (
                    <span className="flex flex-col gap-0.5">
                        <span className="text-neutralBlack-900">{row.trip.title ?? row.itineraryName ?? '—'}</span>
                        <span className="text-xs text-neutralBlack-500">{formatDate(row.trip.createdAt)}</span>
                    </span>
                ) : <span className="text-neutralBlack-400">—</span>}
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                {row.tripbook?.flipbookUrl ? (
                    <a href={row.tripbook.flipbookUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                       className="text-[13px] text-[#3E63DD] hover:underline">
                        {t('packs.viewTripbook')}
                    </a>
                ) : <span className="text-neutralBlack-400">—</span>}
            </td>
            <td className="px-4 py-3">
                <span className="flex items-center gap-1.5 flex-wrap">
                    <AdminCodeStepPill step={row.currentStep}/>
                    {hasIncident && (
                        <span title={t('adminCodes.incidentBadge')} className="inline-flex items-center rounded-full bg-danger/15 text-danger px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
                            {t('adminCodes.incidentBadge')}
                        </span>
                    )}
                </span>
            </td>
            {/* The date is lastUpdatedAt, not activity.lastEventAt: the list arrives
                sorted by it, so showing the narrower date here would leave the row
                order unexplained (a code printed yesterday but created months ago
                sits at the top with an old lastEventAt). */}
            <td className="px-4 py-3 whitespace-nowrap">
                {totalModifications > 0 ? (
                    <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-neutralBlack-900">
                            {t('adminCodes.changesRelevant', {count: totalModifications})}
                        </span>
                        <span title={t('adminCodes.lastUpdatedTooltip')} className="text-xs text-neutralBlack-500">
                            {formatDate(row.lastUpdatedAt)}
                        </span>
                    </span>
                ) : (
                    // Not "sin cambios" — an untouched code still has events (alta, canje...);
                    // saying otherwise next to a two-line history looks like a bug.
                    <span className="flex flex-col gap-0.5">
                        <span className="text-neutralBlack-400">{t('adminCodes.eventsOnly', {count: row.activity.events})}</span>
                        <span title={t('adminCodes.lastUpdatedTooltip')} className="text-xs text-neutralBlack-400">
                            {formatDate(row.lastUpdatedAt)}
                        </span>
                    </span>
                )}
            </td>
        </tr>
    )
}

const Th: React.FC<{children: React.ReactNode; align: 'left' | 'right'}> = ({children, align}) => (
    <th className={`px-4 py-3.5 text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500 border-b border-neutralBlack-100 whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}>
        {children}
    </th>
)
